import { Link } from 'react-router-dom';
import VerticalComparisonMatrix from '../../components/VerticalComparisonMatrix';

function LeadCard({ title, body }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
      <div className="text-sm font-semibold text-[var(--text-primary)]">{title}</div>
      <div className="mt-2 text-sm leading-6 text-[var(--text-secondary)]">{body}</div>
    </div>
  );
}

function Section({ title, subtitle, bullets }) {
  return (
    <div className="rounded-3xl border border-white/10 bg-[var(--gradient-card)] p-7 backdrop-blur-xl">
      <div
        className="text-lg font-semibold tracking-tight"
        style={{ fontFamily: "var(--font-display)" }}
      >
        {title}
      </div>
      {subtitle ? (
        <div className="mt-2 text-sm leading-6 text-[var(--text-secondary)]">{subtitle}</div>
      ) : null}
      <ul className="mt-4 grid gap-2 text-sm text-[var(--text-secondary)]">
        {bullets.map((b) => (
          <li key={b} className="leading-6">
            {b}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Government() {
  return (
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[var(--gradient-hero)] p-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_30%,rgba(212,165,116,0.12),transparent_55%),radial-gradient(circle_at_70%_25%,rgba(37,99,235,0.18),transparent_45%)]" />
        <div className="relative">
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
            Industries
          </div>
          <h1
            className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Government Identity & Trust
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-[var(--text-secondary)] sm:text-base">
            Government organizations face unique identity challenges including citizen-scale identity management,
            inter-agency federation, stringent compliance requirements, and the need to balance security with
            accessibility for diverse populations.
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center rounded-xl bg-[var(--accent-trust)] px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/20"
            >
              Request Government Assessment
            </Link>
            <Link
              to="/architecture"
              className="inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/[0.02] px-5 py-3 text-sm font-semibold text-[var(--text-primary)] hover:bg-white/[0.05]"
            >
              View Reference Architecture
            </Link>
          </div>
        </div>
      </div>

      <div className="mt-14">
        <h2
          className="text-2xl font-semibold tracking-tight"
          style={{ fontFamily: "var(--font-display)" }}
        >
          The Challenge
        </h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <LeadCard title="Citizen-scale identity" body="Millions of citizens, diverse identity documents, and fraud prevention." />
          <LeadCard title="Inter-agency federation" body="Cross-ministry access with strong boundaries, accountability, and auditability." />
          <LeadCard title="Compliance-first posture" body="IT Act, DPDP, CERT-In reporting, and audit readiness expectations." />
          <LeadCard title="Accessibility + security" body="Inclusive authentication for diverse populations across connectivity constraints." />
        </div>
      </div>

      <div className="mt-14">
        <h2
          className="text-2xl font-semibold tracking-tight"
          style={{ fontFamily: "var(--font-display)" }}
        >
          Citizen Identity & Digital Services
        </h2>
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <Section
            title="National Digital Identity (Aadhaar/DigiLocker Integration)"
            bullets={[
              "Unified citizen authentication across government portals using national identity infrastructure with biometric and OTP-based verification",
            ]}
          />
          <Section
            title="Single Sign-On for e-Governance"
            bullets={[
              "Federated SSO enabling citizens to access multiple government services (tax filing, passport, driving license, land records) with single authentication",
            ]}
          />
          <Section
            title="Digital Locker Integration"
            bullets={[
              "Secure document vault integration allowing verified identity documents to be shared across departments without repeated verification",
            ]}
          />
          <Section
            title="Inclusive Authentication"
            bullets={[
              "Multi-modal authentication options (biometric, OTP, face recognition, assisted authentication) ensuring accessibility for elderly, differently-abled, and digitally-challenged citizens",
            ]}
          />
          <Section
            title="Rural & Remote Access"
            bullets={[
              "Offline-capable identity verification for areas with limited connectivity, with secure sync mechanisms",
            ]}
          />
        </div>
      </div>

      <div className="mt-14">
        <h2
          className="text-2xl font-semibold tracking-tight"
          style={{ fontFamily: "var(--font-display)" }}
        >
          Government Workforce Identity
        </h2>
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <Section
            title="Employee Lifecycle Management"
            bullets={[
              "Automated provisioning/deprovisioning synced with HRMS systems covering transfers, promotions, deputation, and retirement across departments",
            ]}
          />
          <Section
            title="Role-Based Access for Classified Systems"
            bullets={[
              "Multi-level security clearance enforcement with need-to-know access controls for defense and sensitive government systems",
            ]}
          />
          <Section
            title="Inter-Department Federation"
            bullets={[
              "Cross-ministry identity federation enabling seamless collaboration while maintaining departmental access boundaries",
            ]}
          />
          <Section
            title="Contractor & Vendor Identity"
            bullets={[
              "Time-bound, project-scoped access for external contractors with automated expiration and audit trails",
            ]}
          />
          <Section
            title="Physical-Logical Access Convergence"
            bullets={[
              "Unified identity for building access, computer login, and secure facility entry with smart card/biometric integration",
            ]}
          />
        </div>
      </div>

      <div className="mt-14">
        <h2
          className="text-2xl font-semibold tracking-tight"
          style={{ fontFamily: "var(--font-display)" }}
        >
          Privileged Access & Critical Systems
        </h2>
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <Section
            title="Critical Infrastructure PAM"
            bullets={[
              "Just-in-time privileged access for power grid, water systems, and transportation control systems with session recording",
            ]}
          />
          <Section
            title="Database Administrator Controls"
            bullets={[
              "Fine-grained database access for systems containing citizen PII (Personally Identifiable Information) with query-level auditing",
            ]}
          />
          <Section
            title="Emergency Break-Glass Procedures"
            bullets={[
              "Controlled emergency access mechanisms for crisis situations with post-incident review workflows",
            ]}
          />
          <Section
            title="Secrets Management"
            bullets={[
              "Centralized management of API keys, certificates, and credentials for government applications and integrations",
            ]}
          />
        </div>
      </div>

      <div className="mt-14">
        <h2
          className="text-2xl font-semibold tracking-tight"
          style={{ fontFamily: "var(--font-display)" }}
        >
          Compliance & Audit
        </h2>
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <Section
            title="IT Act & DPDP Compliance"
            bullets={[
              "Identity controls aligned with Information Technology Act and Digital Personal Data Protection requirements",
            ]}
          />
          <Section
            title="CAG Audit Readiness"
            bullets={["Comprehensive access logs and certification reports for Comptroller and Auditor General reviews"]}
          />
          <Section
            title="RTI Response Automation"
            bullets={["Access history and entitlement reports supporting Right to Information requests"]}
          />
          <Section
            title="CERT-In Incident Reporting"
            bullets={["Identity event logs structured for mandatory cybersecurity incident reporting"]}
          />
        </div>
      </div>

      <div className="mt-14">
        <h2
          className="text-2xl font-semibold tracking-tight"
          style={{ fontFamily: "var(--font-display)" }}
        >
          Identity Security & Threat Detection
        </h2>
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <Section
            title="Insider Threat Detection"
            bullets={["Behavioral analytics identifying anomalous access patterns by government employees"]}
          />
          <Section
            title="Credential Compromise Detection"
            bullets={["Real-time monitoring for leaked government credentials on dark web and hacking forums"]}
          />
          <Section
            title="Nation-State Attack Defense"
            bullets={["Advanced identity threat detection for sophisticated APT-style attacks targeting government systems"]}
          />
          <Section
            title="Election System Protection"
            bullets={["Hardened identity controls for electoral systems with multi-party oversight mechanisms"]}
          />
        </div>
      </div>

      <div className="mt-14 rounded-3xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur-xl sm:p-10">
        <div className="grid gap-8 lg:grid-cols-[1fr_1fr] lg:items-center">
          <div>
            <h2
              className="text-2xl font-semibold tracking-tight"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Outcomes
            </h2>
            <div className="mt-4 grid gap-2 text-sm text-[var(--text-secondary)]">
              {[
                "Secure citizen services at scale",
                "Sovereign control over identity infrastructure",
                "Zero Trust architecture compliance",
                "Reduced identity fraud",
                "Audit-ready operations",
              ].map((o) => (
                <div key={o} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                  {o}
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[var(--gradient-card)] p-8">
            <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
              Next step
            </div>
            <div
              className="mt-3 text-xl font-semibold tracking-tight"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Transform Government Identity Security
            </div>
            <p className="mt-3 text-sm leading-7 text-[var(--text-secondary)]">
              Align citizen identity services, workforce governance, privileged controls, and threat detection to reduce
              breach risk and accelerate audit readiness.
            </p>
            <Link
              to="/contact"
              className="mt-6 inline-flex w-full items-center justify-center rounded-xl bg-[var(--accent-trust)] px-5 py-3 text-sm font-semibold text-white"
            >
              Request Government Assessment
            </Link>
          </div>
        </div>
      </div>

      <VerticalComparisonMatrix />
    </div>
  );
}
